import {none, Option, option} from "ts-option";
import BaseFormController from "../../../validation/BaseFormController";
import {FormField, OptionalFormField, RequiredFormField} from "../../../validation/FormField";
import FormValidation from "../../../validation/FormValidation";
import {ValidationResult} from "../../../validation/ValidationResult";
import QuoteItem from "../../../model/quote/QuoteItem";
import Quote, {QuotePartsPriceStrategy, QuotePrices} from "../../../model/quote/Quote";

const uuidv4 = require("uuid/v4");

/**
 * Product package form data.
 */
export default class QuoteForm extends BaseFormController<QuoteForm> {
	constructor(formId: string,
		readonly description: OptionalFormField<string>,
		readonly brandId: RequiredFormField<string>,
		readonly partnerBrandId: OptionalFormField<string>,
		readonly leadId: RequiredFormField<string>,
		readonly items: RequiredFormField<QuoteItem[]>,
		readonly summary: RequiredFormField<string>,
		readonly labourQuantity: RequiredFormField<number>,
		readonly vat: RequiredFormField<number>,
		readonly markup: RequiredFormField<number>,
		readonly appointmentId: OptionalFormField<string>,
		readonly vatRegistered: RequiredFormField<boolean>,
		readonly labourCost: RequiredFormField<number>,
		readonly partsPriceStrategy: RequiredFormField<string>,
		readonly maxFee: RequiredFormField<number>) {
		super(name, formId);
	}

	static createQuoteForm({
		description = none,
		brandId = none,
		partnerBrandId = none,
		leadId = none,
		items = none,
		summary = none,
		labourQuantity = none,
		vat = none,
		markup = none,
		appointmentId = none,
		vatRegistered = none,
		labourCost = none,
		partsPriceStrategy = none,
		maxFee = none
	}) {
		const labourItemsField = RequiredFormField.create<number>(labourQuantity, "Labour is required");

		return new QuoteForm(
			uuidv4(),
			OptionalFormField.create<string>(description),
			RequiredFormField.create<string>(brandId, "Brand id is required"),
			OptionalFormField.create<string>(partnerBrandId),
			RequiredFormField.create<string>(leadId, "Lead id is required"),
			RequiredFormField.create<QuoteItem[]>(items, 'Asset is required', [
				QuoteForm.validateTotalCost("Total cost must be greater than 0. Please select asset or labour",
					labourItemsField.id)
			]),
			RequiredFormField.create<string>(summary, "Quote title is required"),
			labourItemsField,
			RequiredFormField.create<number>(vat, "Boiler is required"),
			RequiredFormField.create<number>(markup, "Boiler is required"),
			OptionalFormField.create<string>(appointmentId),
			RequiredFormField.create<boolean>(vatRegistered, "VAT registered status is required"),
			RequiredFormField.create<number>(labourCost, "Labour cost is required"),
			RequiredFormField.create<string>(partsPriceStrategy, "Parts price strategy is required"),
			RequiredFormField.create<number>(maxFee, "Max fee is required")
		)
	}

	public static createEmpty(leadId: string,
		brandId: string,
		partnerBrandId: Option<string>,
		vat: number,
		markup: number,
		labourCost: number,
		vatRegistered: boolean,
		maxFee: number): QuoteForm {
		return this.createQuoteForm(
			{
				leadId: option(leadId),
				brandId: option(brandId),
				partnerBrandId: partnerBrandId,
				vat: option(vat),
				markup: option(markup),
				labourQuantity: option(0),
				items: option([]),
				vatRegistered: option(vatRegistered),
				labourCost: option(labourCost),
				partsPriceStrategy: option(QuotePartsPriceStrategy.BEST_AVAILABILITY),
				maxFee: option(maxFee)
			});
	}

	static validateTotalCost(errorsMsg: string, labourItemsFieldId: string) {
		return (form: BaseFormController<QuoteForm>, thisValue: QuoteItem[]): string[] => {
			if (Quote.calculateQuoteItemTotalCost(thisValue).cents > 0) {
				return [];
			}
			const labourField: Option<OptionalFormField<number>> = form.findFieldById<OptionalFormField<number>>(
				labourItemsFieldId);
			let labourCosts = labourField.get.value.getOrElse(0);
			return labourCosts > 0 ? [] : [errorsMsg];
		}
	}

	validate(): QuoteForm {
		return FormValidation.validateForm<QuoteForm>(this, (form, config) => form.copy(config));
	}

	onValid(onValid: (pp: Quote) => void,
		onInvalid: () => void): ValidationResult<Quote> {
		return FormValidation.validateFields(
			[this.brandId, this.leadId, this.items, this.vat, this.markup, this.labourQuantity, this.summary, this.labourCost,
				this.partsPriceStrategy, this.maxFee],
			() => {
				const quotePrices: QuotePrices = Quote.calculatePrices(this.vat.getValue(), this.markup.getValue(),
					this.items.getValue(), this.labourQuantity.getValue(), this.vatRegistered.getValue(),
					this.labourCost.getValue(), this.maxFee.getValue());
				return new Quote(uuidv4, this.appointmentId.getOptionalValue(), this.leadId.getOptionalValue(),
					this.brandId.getValue(), this.partnerBrandId.getOptionalValue(),
					this.vat.getValue(), this.items.getValue(), this.summary.getValue(), this.description.getOptionalValue(),
					quotePrices.labourTotalCost, quotePrices.totalPrice, quotePrices.totalPrice, quotePrices.totalBeforeVat,
					quotePrices.vatAmount, this.markup.getValue(), quotePrices.markupAmount, this.vatRegistered.getValue(),
					option(quotePrices.hiberCommission), option(quotePrices.installerGross), this.partsPriceStrategy.getValue(),
					this.maxFee.getValue()
				);
			}).onSuccess(pp => onValid(pp)).onFailure(() => onInvalid());
	}

	copy({
		description = this.description,
		brandId = this.brandId,
		partnerBrandId = this.partnerBrandId,
		leadId = this.leadId,
		items = this.items,
		summary = this.summary,
		labourQuantity = this.labourQuantity,
		vat = this.vat,
		markup = this.markup,
		appointmentId = this.appointmentId,
		vatRegistered = this.vatRegistered,
		labourCost = this.labourCost,
		partsPriceStrategy = this.partsPriceStrategy,
		maxFee = this.maxFee,
	}): QuoteForm {
		return new QuoteForm(this.formId, description, brandId, partnerBrandId, leadId, items, summary,
			labourQuantity, vat, markup, appointmentId, vatRegistered, labourCost, partsPriceStrategy, maxFee)
	}

	fields(): Array<FormField<any>> {
		return [this.brandId, this.leadId, this.items, this.vat, this.markup, this.labourQuantity,
			this.summary, this.vatRegistered];
	}
}
